import React, { useState, useEffect, useRef } from 'react';
import { detectPlatform, getInstallInstructions } from '../../utils/pwa/platformDetection';
import {
  shouldShowPrompt,
  recordPromptShown,
  recordPromptDismissed,
  recordPromptLater,
  recordVisit,
  recordTimeSpent
} from '../../utils/pwa/installPromptTiming';
import { saveAppState } from '../../utils/pwa/deepLinking';
import './PWAInstallPrompt.css';

interface PWAInstallPromptProps {
  className?: string;
}

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const PWAInstallPrompt: React.FC<PWAInstallPromptProps> = ({ className }) => {
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const { isIOS, isAndroid, isDesktop, isStandalone } = detectPlatform();
  
  // Track time spent in the app
  const timeIntervalRef = useRef<number | null>(null);
  
  // Record visit and start tracking time spent
  useEffect(() => {
    // Record a visit when the component mounts
    recordVisit();
    console.log('Visit recorded. Checking if prompt should be shown...');
    
    // Log current metrics for debugging
    const metrics = JSON.parse(localStorage.getItem('veripay_pwa_metrics') || '{}');
    console.log('Current metrics:', metrics);
    
    // Check if we should show the prompt immediately after recording a visit
    if (!isVisible && !isStandalone && shouldShowPrompt()) {
      console.log('Showing installation prompt...');
      setIsVisible(true);
      recordPromptShown();
    } else {
      console.log('Not showing prompt yet. Will check again after time update.');
    }
    
    // Set up timer to track time spent (every minute)
    const intervalTime = 30 * 1000; // 30 seconds for faster testing (change to 60 * 1000 for production)
    timeIntervalRef.current = window.setInterval(() => {
      recordTimeSpent(intervalTime);
      console.log(`Time spent updated: +${intervalTime}ms`);
      
      // Log current metrics for debugging
      const updatedMetrics = JSON.parse(localStorage.getItem('veripay_pwa_metrics') || '{}');
      console.log('Updated metrics:', updatedMetrics);
      
      // Check if we should show the prompt after updating time
      if (!isVisible && !isStandalone && shouldShowPrompt()) {
        console.log('Time threshold reached. Showing installation prompt...');
        setIsVisible(true);
        recordPromptShown();
      } else {
        console.log('Not showing prompt yet after time update.');
      }
    }, intervalTime);
    
    // Clean up timer when component unmounts
    return () => {
      if (timeIntervalRef.current !== null) {
        window.clearInterval(timeIntervalRef.current);
      }
    };
  }, [isVisible, isStandalone]);
  
  useEffect(() => {
    // Don't show if already installed
    if (isStandalone) return;
    
    // Listen for beforeinstallprompt event
    const handleBeforeInstallPrompt = (e: Event) => {
      console.log('beforeinstallprompt event fired');
      
      // Prevent Chrome 76+ from automatically showing the prompt
      e.preventDefault();
      // Stash the event so it can be triggered later
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      
      // Check if we should show the prompt
      if (shouldShowPrompt()) {
        console.log('Showing installation prompt after beforeinstallprompt event...');
        setIsVisible(true);
        recordPromptShown();
      } else {
        console.log('Not showing prompt after beforeinstallprompt event (conditions not met).');
      }
    };
    
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    
    // For iOS, we need to check manually
    if (isIOS) {
      console.log('iOS device detected');
      if (shouldShowPrompt()) {
        console.log('Showing installation prompt for iOS device...');
        setIsVisible(true);
        recordPromptShown();
      } else {
        console.log('Not showing prompt for iOS device yet (conditions not met).');
      }
    }
    
    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, [isIOS, isStandalone]);
  
  const handleInstall = async () => {
    // Save the current app state for deep linking
    saveAppState();
    
    if (deferredPrompt) {
      // Show the install prompt
      deferredPrompt.prompt();
      
      // Wait for the user to respond to the prompt
      const { outcome } = await deferredPrompt.userChoice;
      console.log(`User response to the install prompt: ${outcome}`);
      
      // We've used the prompt, and can't use it again, discard it
      setDeferredPrompt(null);
    }
    
    setIsVisible(false);
  };
  
  const handleLater = () => {
    console.log('User clicked "Remind Me Later"');
    setIsVisible(false);
    recordPromptLater();
  };
  
  const handleDismiss = () => {
    console.log('User clicked "No Thanks" or close button');
    setIsVisible(false);
    recordPromptDismissed();
  };
  
  if (!isVisible) return null;
  
  // Add platform-specific class for styling
  const platformClass = isIOS ? 'ios' : isAndroid ? 'android' : 'desktop';
  
  return (
    <div className={`pwa-install-prompt ${platformClass} ${className || ''}`}>
      <div className="pwa-install-prompt-content">
        <div className="pwa-install-prompt-icon">
          <img src="/VeriPay-Icon.jpg" alt="VeriPay" />
        </div>
        <div className="pwa-install-prompt-message">
          <h3>Install VeriPay for faster payments</h3>
          <p>{getInstallInstructions()}</p>
        </div>
        <div className="pwa-install-prompt-actions">
          <button 
            className="pwa-install-prompt-install-button"
            onClick={handleInstall}
          >
            Install Now
          </button>
          <button 
            className="pwa-install-prompt-later-button"
            onClick={handleLater}
          >
            Remind Me Later
          </button>
          <button 
            className="pwa-install-prompt-dismiss-button"
            onClick={handleDismiss}
          >
            No Thanks
          </button>
        </div>
        <button 
          className="pwa-install-prompt-close-button"
          onClick={handleDismiss}
        >
          ✕
        </button>
      </div>
    </div>
  );
};

export default PWAInstallPrompt;